// src/pages/NewVisitNote.jsx

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { Save, ArrowLeft, CheckCircle, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import SmartCaptureModal from "../components/SmartCaptureModal";
import { apiPost } from "../utils/api"; // ✅ unified backend helper

// --------------------------------------------------------
// Reusable Textarea
// --------------------------------------------------------
const NoteSectionTextarea = ({ label, ...props }) => (
  <div>
    <label className="block text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
      {label}
    </label>
    <textarea
      {...props}
      className="w-full h-40 bg-slate-100 dark:bg-zinc-800 border-none rounded-lg p-4 text-base text-gray-800 dark:text-white placeholder-gray-400 
                 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
    />
  </div>
);

export default function NewVisitNote() {
  const { patientId, noteId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [showSmartCapture, setShowSmartCapture] = useState(false);

  const [noteData, setNoteData] = useState({
    hpi: "",
    reviewOfSystems: "",
    pastMedicalHistory: "",
    pastSurgicalHistory: "",
    familyHistory: "",
    medications: "",
    allergies: "",
    socialHistory: "",
    exam: "",
    assessmentPlan: "",
    type: "Clinic Visit",
  });

  const [patientName, setPatientName] = useState("");
  const [activeTab, setActiveTab] = useState("Visit");
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFixing, setIsFixing] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const noteTabs = ["Visit", "History", "Meds", "Lifestyle", "Screening"];

  // --------------------------------------------------------
  // Fetch Patient
  // --------------------------------------------------------
  const fetchPatientName = useCallback(async () => {
    if (!patientId) return;

    const ref = doc(db, "patients", patientId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const d = snap.data();
      setPatientName(`${d.firstName || ""} ${d.lastName || ""}`.trim());
    }
  }, [patientId]);

  useEffect(() => {
    const init = async () => {
      setErrorMsg("");
      fetchPatientName();

      if (noteId && noteId !== "new") {
        const ref = doc(db, "notes", noteId);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          setNoteData(prev => ({ ...prev, ...snap.data() }));
        }
      }
    };

    init();
  }, [noteId, patientId, fetchPatientName]);

  // --------------------------------------------------------
  // Save Note
  // --------------------------------------------------------
  const handleSave = async () => {
    setIsSaving(true);
    setIsSaved(false);

    const summary = `HPI:\n${noteData.hpi}\n\nA&P:\n${noteData.assessmentPlan}`;
    const payload = { ...noteData, summary, timestamp: serverTimestamp() };

    try {
      if (noteId && noteId !== "new") {
        await setDoc(doc(db, "notes", noteId), payload, { merge: true });
      } else {
        const newRef = await addDoc(collection(db, "notes"), {
          ...payload,
          ownerId: currentUser.uid,
          patientID: patientId,
        });

        navigate(`/new-visit-note/${patientId}/${newRef.id}`, {
          replace: true,
        });
      }

      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 1800);
    } catch (err) {
      setErrorMsg(err.message || "Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  // --------------------------------------------------------
  // Fix HPI → /v1/format-hpi
  // --------------------------------------------------------
  const handleFixHpi = async () => {
    setIsFixing("hpi");
    setErrorMsg("");

    try {
      const result = await apiPost("/format-hpi", {
        hpi: noteData.hpi,
        pmh: noteData.pastMedicalHistory,
        ros: noteData.reviewOfSystems,
        patient_name: patientName || null,
      });

      setNoteData(prev => ({
        ...prev,
        hpi: result.formatted_hpi || prev.hpi,
      }));
    } catch (err) {
      setErrorMsg(err.message || "Fix HPI failed.");
    } finally {
      setIsFixing(null);
    }
  };

  // --------------------------------------------------------
  // Fix Medications → /v1/normalize-medications
  // --------------------------------------------------------
  const handleFixMeds = async () => {
    setIsFixing("meds");
    setErrorMsg("");

    try {
      const result = await apiPost("/normalize-medications", {
        text: noteData.medications,
      });

      setNoteData(prev => ({
        ...prev,
        medications: result.summary || prev.medications,
      }));
    } catch (err) {
      setErrorMsg(err.message || "Fix meds failed.");
    } finally {
      setIsFixing(null);
    }
  };

  // --------------------------------------------------------
  // Generate A&P → /v1/generate-ap-v2
  // --------------------------------------------------------
  const handleGenerateAP = async () => {
    setIsGenerating(true);
    setErrorMsg("");

    const current_note_context = `
HPI: ${noteData.hpi}
ROS: ${noteData.reviewOfSystems}
Exam: ${noteData.exam}
`;

    try {
      const result = await apiPost("/generate-ap-v2", {
        context: "outpatient",
        current_note_context,
      });

      setNoteData(prev => ({
        ...prev,
        assessmentPlan: result.summary || prev.assessmentPlan,
      }));
    } catch (err) {
      setErrorMsg(err.message || "A&P generation failed.");
      setNoteData(prev => ({
        ...prev,
        assessmentPlan: `Error: ${err.message}`,
      }));
    } finally {
      setIsGenerating(false);
    }
  };

  // --------------------------------------------------------
  // Smart Capture → /v1/format-outpatient-text
  // --------------------------------------------------------
  const handleSmartCaptureSubmit = async text => {
    try {
      const r = await apiPost("/format-outpatient-text", { text });

      setNoteData(prev => ({
        ...prev,
        hpi: r.hpi || prev.hpi,
        reviewOfSystems: r.review_of_systems || prev.reviewOfSystems,
        pastMedicalHistory: r.past_medical_history || prev.pastMedicalHistory,
        pastSurgicalHistory: r.past_surgical_history || prev.pastSurgicalHistory,
        familyHistory: r.family_history || prev.familyHistory,
        socialHistory: r.social_history || prev.socialHistory,
        medications: r.medications || prev.medications,
        allergies: r.allergies || prev.allergies,
        exam: r.exam || prev.exam,
        assessmentPlan: r.assessment_plan || prev.assessmentPlan,
      }));
    } catch (err) {
      console.error("SmartCapture error:", err);
      setErrorMsg(err.message || "Smart Capture failed.");
    }
  };

  // --------------------------------------------------------
  // Render
  // --------------------------------------------------------
  const handleChange = e =>
    setNoteData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />

      <main className="max-w-5xl mx-auto py-10 px-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl">

          {/* HEADER */}
          <div className="p-6 border-b border-gray-200 dark:border-zinc-800">
            <div className="flex items-center justify-between">
              
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800"
                >
                  <ArrowLeft size={20} />
                </button>

                <div>
                  <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                    New Visit Note
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400">
                    for {patientName || "patient"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSmartCapture(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
                >
                  <Sparkles size={16} /> Smart Capture
                </button>

                <button
                  onClick={handleSave}
                  disabled={isSaving || isSaved}
                  className={`flex items-center justify-center gap-2 px-8 py-2 rounded-full font-semibold text-white transition-all duration-300 ${
                    isSaved ? "bg-green-500" : "bg-blue-500 hover:bg-blue-600"
                  }`}
                >
                  {isSaving ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isSaved ? (
                    <>
                      <CheckCircle size={18} /> Saved!
                    </>
                  ) : (
                    <>
                      <Save size={18} /> Save Note
                    </>
                  )}
                </button>
              </div>
            </div>

            {errorMsg && (
              <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                {errorMsg}
              </div>
            )}
          </div>

          {/* TABS */}
          <div className="p-6">
            <div className="flex justify-center mb-8">
              <div className="flex items-center space-x-2 bg-slate-200/70 dark:bg-zinc-800 rounded-full p-1">
                {noteTabs.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className="relative px-5 py-1.5 text-sm font-semibold transition-colors duration-300"
                  >
                    <span
                      className={`relative z-10 ${
                        activeTab === tab
                          ? "text-gray-800 dark:text-white"
                          : "text-gray-500 hover:text-gray-800 dark:hover:text-white"
                      }`}
                    >
                      {tab}
                    </span>

                    {activeTab === tab && (
                      <motion.div
                        layoutId="active-note-pill"
                        className="absolute inset-0 bg-white dark:bg-zinc-700 rounded-full shadow-sm"
                        transition={{ type: "spring", stiffness: 350, damping: 30 }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Visit Tab */}
            {activeTab === "Visit" && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  label="Reason for Visit / HPI"
                  name="hpi"
                  value={noteData.hpi}
                  onChange={handleChange}
                />

                <div className="flex justify-end">
                  <button
                    onClick={handleFixHpi}
                    disabled={isFixing === "hpi"}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-full font-semibold hover:bg-indigo-700 transition"
                  >
                    {isFixing === "hpi" ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>✨ Fix HPI</>
                    )}
                  </button>
                </div>

                <NoteSectionTextarea
                  label="Review of Systems"
                  name="reviewOfSystems"
                  value={noteData.reviewOfSystems}
                  onChange={handleChange}
                />
              </div>
            )}

            {/* History Tab */}
            {activeTab === "History" && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  label="Past Medical History"
                  name="pastMedicalHistory"
                  value={noteData.pastMedicalHistory}
                  onChange={handleChange}
                />
                <NoteSectionTextarea
                  label="Past Surgical History"
                  name="pastSurgicalHistory"
                  value={noteData.pastSurgicalHistory}
                  onChange={handleChange}
                />
                <NoteSectionTextarea
                  label="Family History"
                  name="familyHistory"
                  value={noteData.familyHistory}
                  onChange={handleChange}
                />
              </div>
            )}

            {/* Meds */}
            {activeTab === "Meds" && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  label="Medications"
                  name="medications"
                  value={noteData.medications}
                  onChange={handleChange}
                />

                <div className="flex justify-end mt-2">
                  <button
                    onClick={handleFixMeds}
                    disabled={isFixing === "meds"}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-full font-semibold hover:bg-emerald-700 transition"
                  >
                    {isFixing === "meds" ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>💊 Fix Meds</>
                    )}
                  </button>
                </div>

                <NoteSectionTextarea
                  label="Allergies"
                  name="allergies"
                  value={noteData.allergies}
                  onChange={handleChange}
                />
              </div>
            )}

            {/* Lifestyle */}
            {activeTab === "Lifestyle" && (
              <NoteSectionTextarea
                label="Social History"
                name="socialHistory"
                value={noteData.socialHistory}
                onChange={handleChange}
              />
            )}

            {/* Screening */}
            {activeTab === "Screening" && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  label="Physical Exam"
                  name="exam"
                  value={noteData.exam}
                  onChange={handleChange}
                />

                <NoteSectionTextarea
                  label="Assessment & Plan"
                  name="assessmentPlan"
                  value={noteData.assessmentPlan}
                  onChange={handleChange}
                />

                <button
                  onClick={handleGenerateAP}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
                >
                  {isGenerating ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Sparkles size={16} /> Generate A&P
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Smart Capture */}
      <SmartCaptureModal
        show={showSmartCapture}
        onClose={() => setShowSmartCapture(false)}
        onTextExtracted={handleSmartCaptureSubmit}
      />
    </div>
  );
}
